import type { EntityType, Entity, Attribute, AttributeDataType } from '../types';
import type { ColumnDef } from '../config/viewsConfig';
import { limsClient } from '../api/limsClient';

export interface DiscoveredJsonKey {
  key: string;
  path: string;
  attrName: string;
  hasValueSub: boolean;
}

export interface DiscoveredGroup {
  id: string;
  label: string;
  attrs: { name: string; data_type: AttributeDataType }[];
  jsonKeys?: DiscoveredJsonKey[];
}

/**
 * Sample up to N entities and discover top-level keys in JSON attributes.
 * Shared by ColumnPicker (for column discovery) and EntityFilters (for filter discovery).
 */
export function discoverJsonKeysFromEntities(
  entities: Entity[],
  jsonAttrNames: string[],
  entityType: EntityType,
): { attrName: string; keys: DiscoveredJsonKey[] }[] {
  const sampled = entities.slice(0, 5);
  if (sampled.length === 0) return [];

  const result: { attrName: string; keys: DiscoveredJsonKey[] }[] = [];
  for (const attrName of jsonAttrNames) {
    const keyMap = new Map<string, any>();
    for (const entity of sampled) {
      const blob = entity.attributes?.[attrName];
      if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
        for (const [k, v] of Object.entries(blob)) {
          if (!keyMap.has(k)) keyMap.set(k, v);
        }
      }
    }
    if (keyMap.size === 0) continue;

    const keys: DiscoveredJsonKey[] = [];
    const sortedKeys = Array.from(keyMap.entries()).sort(([a], [b]) => a.localeCompare(b));
    for (const [key, sampleValue] of sortedKeys) {
      const hasValueSub = sampleValue && typeof sampleValue === 'object' && 'value' in sampleValue;
      keys.push({
        key,
        path: hasValueSub ? `${key}.value` : key,
        attrName,
        hasValueSub,
      });
    }
    result.push({ attrName, keys });
  }
  return result;
}

/**
 * Build ColumnDef entries from discovered JSON keys.
 * Used by ColumnPicker.
 */
export function jsonKeysToColumnDefs(
  discovered: { attrName: string; keys: DiscoveredJsonKey[] }[],
  entityType: EntityType,
): { id: string; label: string; columns: ColumnDef[] }[] {
  return discovered.map(({ attrName, keys }) => ({
    id: `json_${attrName}`,
    label: `${attrName} (via ${entityType})`,
    columns: keys.map(k => ({
      key: `${attrName}_${k.key}`,
      label: k.key,
      extract: { from: attrName, path: k.path },
      sortable: false,
    })),
  }));
}

/**
 * Load direct + FK attributes for an entity type.
 * Shared by ColumnPicker and EntityFilters modals.
 */
export async function loadEntityAttributes(
  entityType: EntityType,
): Promise<{
  directAttrs: Attribute[];
  fkAttrs: Attribute[];
  jsonAttrNames: string[];
}> {
  const attrs = await limsClient.getAttributes(entityType);
  const directAttrs: Attribute[] = [];
  const jsonAttrNames: string[] = [];

  for (const a of attrs) {
    if (a.data_type === 'json') {
      jsonAttrNames.push(a.name);
    } else {
      directAttrs.push(a);
    }
  }

  const fkAttrs = attrs.filter(a => a.referenced_entity_type);
  return { directAttrs, fkAttrs, jsonAttrNames };
}

/**
 * Load attributes for related entity types (via FK).
 * Returns one group per FK attribute.
 */
export async function loadFkAttributeGroups(
  fkAttrs: Attribute[],
  cancelled: () => boolean,
): Promise<{ id: string; label: string; fkAttrName: string; refType: EntityType; attrs: Attribute[] }[]> {
  const groups: { id: string; label: string; fkAttrName: string; refType: EntityType; attrs: Attribute[] }[] = [];

  await Promise.all(fkAttrs.map(async (fkAttr) => {
    const refType = fkAttr.referenced_entity_type!;
    try {
      const refAttrs = await limsClient.getAttributes(refType);
      if (cancelled()) return;
      const nonJsonAttrs = refAttrs.filter(ra => ra.data_type !== 'json');
      groups.push({
        id: `fk_${fkAttr.name}`,
        label: `${refType.charAt(0).toUpperCase() + refType.slice(1)} (via ${fkAttr.name})`,
        fkAttrName: fkAttr.name,
        refType,
        attrs: nonJsonAttrs,
      });
    } catch { /* skip */ }
  }));

  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}
